import { axiosInstance } from '../utils/axios/requestInfo';
import { generateGuid } from './generateGuid';
import {
  ICreatePaymentLinkParams,
  ICreatePaymentLinkResult,
} from './paymentTypes';

/**
 * Создание платежа Т-Кассы.
 *
 * В отличие от ЮKassa, которую сайт создаёт сам (`createPaymentLink`), запрос
 * в Т-Кассу подписывается паролем терминала — в браузер его отдавать нельзя,
 * поэтому Init идёт ЧЕРЕЗ НАШ БЭКЕНД (`POST /api/payments/create`,
 * `provider: 'tinkoff'`). Пароль живёт только в env бэкенда.
 *
 * Тело собираем в формате ЮKassa (рубли строкой + receipt) — бэкенд сам
 * переводит в копейки и Receipt Т-Кассы. Результат возвращаем В ТОМ ЖЕ виде,
 * что и `createPaymentLink`, чтобы вся дальнейшая логика страницы оплаты
 * (префей в Bitrix, переход на /payment/confirm) работала без изменений.
 *
 * Привязанные карты не поддерживаются: токены карт — от ЮKassa, у Тинькофф это
 * отдельный механизм (Rebill). Поэтому cardId игнорируется, платим новой картой.
 */
const TEST_PHONES = ['+79251590433', '+79998360292'];

export const createTinkoffPaymentLink = async (
  params: ICreatePaymentLinkParams
): Promise<ICreatePaymentLinkResult> => {
  const {
    totalPrice,
    phone,
    description,
    partnerId,
    skipTestPhoneCheck = false,
  } = params;

  const guid = generateGuid({
    phone: partnerId,
    name: String(Math.random()),
    email: new Date().toISOString(),
  });

  const finalPrice =
    skipTestPhoneCheck || !TEST_PHONES.includes(phone) ? totalPrice : 1;
  const amountValue = parseFloat(finalPrice.toString()).toFixed(2);

  const body = {
    provider: 'tinkoff',
    amount: {
      value: amountValue,
      currency: 'RUB',
    },
    description: `Оплата ${description}`,
    receipt: {
      customer: {
        phone: phone,
      },
      items: [
        {
          quantity: 1,
          amount: {
            value: amountValue,
            currency: 'RUB',
          },
          vat_code: 1,
          description: `Оплата ${description}`,
        },
      ],
    },
    metadata: {
      order_id: guid,
      partner_id: String(partnerId),
      phone: phone,
    },
  };

  try {
    const response = await axiosInstance.post('/payments/create', body);
    const data = response.data || {};

    return {
      success: true,
      paymentId: data.id ? String(data.id) : undefined,
      confirmationUrl: data.confirmation_url || data.payment_url || undefined,
      status: data.status,
      hasSavedCard: false,
      guid: guid,
    };
  } catch (error: any) {
    const errorData = error.response?.data || error.message;

    return {
      success: false,
      error: errorData,
      errorMessage:
        errorData?.message ||
        errorData?.description ||
        'Ошибка создания платежа Т-Кассы',
      guid: guid,
    };
  }
};

/**
 * Статус платежа Т-Кассы (GetState через бэкенд).
 * Ответ приведён к тому же виду, что отдаёт `getPaymentInfo` для ЮKassa
 * (`status`, `paid`, `cancellation_details`), чтобы polling не переписывать.
 */
export const getTinkoffPaymentInfo = async (paymentId: string): Promise<any> => {
  const response = await axiosInstance.get(`/payments/${paymentId}/status`);
  return response.data;
};
